import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EVENTS } from "~/common/constants/events";
import {
  DBProduct,
  ProductType,
  normalizeUrl,
  priceCalculator,
  priceWithWholesale,
  useAnalytics,
} from "@commons-monorepo/index";
import {
  useAppNavigate,
  useAppParams,
  useAppSearchParams,
} from "@commons-monorepo/index";
import { ReviewItem } from "~/components/Reviews/reviews.types";
import { allowSpecialCharacters } from "../catalogue/utils/format";
import {
  availabilityDataDisplayCopy,
  availabilityDataTextCopy,
} from "./copies";
import { fetchProductsRecomend, fetchReviews } from "./Products.fetcher";
import { ProductProps, ToppingValueDto } from "./Product.type";
import { BreadcrumbsRoute } from "~/components/breadcrumbs/breadcrumbs.types";
import { decamelizeKeys } from "humps";
import { PRODUCT_EVENTS } from "./product.events";
import { mobileNavigatorUserAgents } from "../catalogue/components/share-store/share-store.constants";
import { useGlobalStyles } from "~/styles/context/global-styles-wrapper";
import {
  categoryNormalize,
  makeUrl,
  getVariantByToppings,
} from "@commons-monorepo/index";
import { Product } from "~/common/props/orchestrator";
import { useLocation, useOutletContext } from "@remix-run/react";
import { useCart } from "~/modules/cart/providers/cart.provider";
import {
  COMPONENTS_FOR_DEFAULT,
  COMPONENTS_FOR_PRICE_FIXED,
} from "./constants/components.constants";

export const useLogicProduct = ({
  product,
  partnerId,
  globalOffers,
  dbProducts,
  offerId,
}: ProductProps) => {
  const { storeMetadata } = useOutletContext<any>();
  const [logEvent] = useAnalytics({ storeMetadata });
  const navigate = useAppNavigate();
  const { t } = useTranslation();
  const { productId, comboId } = useAppParams();
  const [searchParams, setSearchParams] = useAppSearchParams();
  const { theme } = useGlobalStyles();
  const {
    setSource,
    addProduct,
    cartProducts,
    goToCheckout: goToCheckoutFn,
  } = useCart();
  const { pathname } = useLocation();

  const [products, setProducts] = useState<Product[]>([]);
  const [toppings, setToppings] = useState<ToppingValueDto[]>([]);
  const [source] = searchParams.getAll("source");
  const [sourceEvent] = searchParams.getAll("source_event");
  const [reviewsStars, setReviewsStars] = useState<number>(0);
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const offer = !!globalOffers?.length ? globalOffers[0] : undefined;
  const reviewsRef = useRef<null | HTMLDivElement>(null);

  const ownerId = storeMetadata.configuration?.ownerId || partnerId;
  const addCardProductFixed =
    storeMetadata.abCartAddProduct?.treatment === "on";

  const componentsForPage = addCardProductFixed
    ? COMPONENTS_FOR_PRICE_FIXED
    : COMPONENTS_FOR_DEFAULT;

  const productsRandom = useMemo(
    () =>
      [...(products || [])]
        .filter((item) => item.id !== product.id)
        .sort(() => 0.5 - Math.random())
        .slice(0, 3),
    [product, products],
  );
  const productUrl = `${comboId ? "combo" : "producto"}/${
    normalizeUrl(product.friendlyUrl) || product.id
  }`;

  const productVariant = useMemo(
    () =>
      getVariantByToppings(product.productToppingVariations || [], toppings),
    [product, toppings],
  );

  const inventoryConfig = useMemo(
    () => productVariant?.inventory || product.inventory,
    [productVariant, product],
  );

  useEffect(() => {
    if (product?.id && ownerId) {
      fetchReviews(ownerId, product.id)
        .then((response: ReviewItem[]) => {
          setReviews(response);
          const stars = response.reduce((acc: number, review: any) => {
            return acc + review.stars;
          }, 0);
          if (stars > 0) {
            setReviewsStars(Math.round(stars / response.length));
          }
        })
        .catch(() => {
          setReviews([]);
          setReviewsStars(0);
        });
    }
  }, [product]);

  useEffect(() => {
    if (source) {
      setSource(source);
    }
  }, [source]);

  useEffect(() => {
    const templateProperties = decamelizeKeys({
      templateId: theme.templateId,
      headerId: theme.template.HEADER,
      bannerId: theme.template.COVER,
      categoriesId: theme.template.CATEGORIES,
      cardId: theme.template.PRODUCT_CARD,
      footerId: theme.template.FOOTER,
      storeInfoId: theme.template.STORE_INFO.SKIN,
    });

    if (productId || comboId) {
      logEvent(EVENTS.VIEW_PRODUCT, {
        productId: productId || comboId || "",
        partnerId: partnerId,
        source: sourceEvent || source || null,
        rawProductId: product.id,
        ...(addCardProductFixed ? { ab_test: "add_to_cart_fixed" } : {}),
        ...templateProperties,
      });
    }
  }, [logEvent]);
  const availabilityDataText = useMemo(
    () =>
      availabilityDataTextCopy(
        t,
        inventoryConfig?.type?.toUpperCase(),
        inventoryConfig,
      ),
    [product, inventoryConfig],
  );
  const inventorydisplayText = useMemo(
    () =>
      availabilityDataDisplayCopy(
        t,
        inventoryConfig?.type?.toUpperCase(),
        inventoryConfig,
      ),
    [product, inventoryConfig],
  );
  const inventoryDisplayAlertColor = useMemo(
    () =>
      inventorydisplayText == t("inventory.last_units") ? "warning" : "success",
    [inventorydisplayText],
  );
  const inventoryDisplayIcon = useMemo(
    () =>
      inventorydisplayText == t("inventory.last_units")
        ? "icon-info-alert-icon"
        : "icon-ray",
    [inventorydisplayText],
  );

  const goBack = () => {
    window.history.state?.usr?.prevPath
      ? navigate(-1)
      : navigate(`/catalogo/${partnerId}`);
  };

  const handleShareProduct = (): void => {
    if (
      navigator?.share &&
      mobileNavigatorUserAgents.test(navigator?.userAgent)
    ) {
      const url = location.href.includes("catalogo")
        ? `${location.origin}/catalogo/${partnerId}`
        : location.origin;
      const text = `${t(
        "share.details.product.title",
      )} *${allowSpecialCharacters(product.name || "")}* ${t(
        "share.details.product.link",
      )}\n${url}/${productUrl}`;
      navigator.share({ text });
    } else {
      searchParams.set("share", "");
      setSearchParams(searchParams);
    }

    logEvent(PRODUCT_EVENTS.SELECT_SHARE_PRODUCT);
  };

  const handleReviewsClick = () => {
    reviewsRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  };

  const breadcrumbsRoutes = useMemo((): BreadcrumbsRoute[] => {
    const routes: BreadcrumbsRoute[] = [];
    if (product.category) {
      const nameCategory = (
        product?.categoryDetail?.name ||
        product.category ||
        ""
      ).trim();
      routes.push({
        text: `${nameCategory.slice(0, 30)}${
          nameCategory.length > 30 ? "..." : ""
        }`,
        to: makeUrl(
          pathname,
          `categoria/${categoryNormalize(product.category)}`,
        ),
      });
    }
    const nameProduct = (product.name || "").trim();
    routes.push({
      text: `${nameProduct.slice(0, 30)}${
        nameProduct.length > 30 ? "..." : ""
      }`,
    });
    return routes;
  }, [product, partnerId]);

  const hasProductInTheCart = useMemo((): boolean => {
    return !!dbProducts.find((p) => p.id === product.id);
  }, [dbProducts, product]);

  const goToCheckout = ({
    skipCart,
    product,
  }: { skipCart?: boolean; product?: DBProduct } = {}) => {
    logEvent(EVENTS.SELECT_VIEW_CART, {
      source: "normal-checkout",
    });
    goToCheckoutFn({ skipCart, product });
  };

  const onViewStoreReviews = () => {
    logEvent(EVENTS.SELECT_STORE_REVIEWS, {});
  };

  const handleChangeToppings = (newToppings: ToppingValueDto[]) => {
    setToppings(newToppings);
    logEvent(PRODUCT_EVENTS.SELECT_VARIATION);
  };

  const handleToCheckoutPay = () => {
    let dbProduct;
    if (!hasProductInTheCart) {
      dbProduct = createDbProduct();
    }
    logEvent(
      PRODUCT_EVENTS.SELECT_BUY_NOW,
      addCardProductFixed ? { ab_test: "add_to_cart_fixed" } : {},
    );
    goToCheckout({ skipCart: true, product: dbProduct });
  };

  const createDbProduct = () => {
    if (product.type === ProductType.Service) {
      const hasAddedService = !!cartProducts.find((cp) => cp.id === product.id);
      if (hasAddedService) return;
    }

    let variationId;
    if (product.productToppingVariations) {
      const variant = getVariantByToppings(
        product.productToppingVariations,
        toppings || [],
      );
      variationId = variant?.id;
    }

    return {
      id: product.id,
      quantity: 1,
      toppings: toppings || [],
      price: priceCalculator(product, toppings),
      wholesalePrice: priceWithWholesale(product, toppings),
      wholesale: product.wholesale,
      type: product.type,
      variationId,
      offerId,
    } as DBProduct;
  };

  useEffect(() => {
    if (product?.id && ownerId) {
      fetchProductsRecomend(ownerId)
        .then((response: Product[]) => {
          setProducts(response);
        })
        .catch(() => {
          setProducts([]);
        });
    }
  }, [partnerId]);

  return {
    t,
    theme,
    logEvent,
    reviewsRef,

    toppings,
    offer,
    reviews,
    reviewsStars,
    productsRandom,
    availabilityDataText,
    inventorydisplayText,
    inventoryDisplayAlertColor,
    inventoryDisplayIcon,
    productVariant,
    inventoryConfig,
    addCardProductFixed,
    breadcrumbsRoutes,
    hasProductInTheCart,
    componentsForPage,

    handleShareProduct,
    handleReviewsClick,
    goBack,
    goToCheckout,
    onViewStoreReviews,
    handleChangeToppings,
    handleToCheckoutPay,
  };
};
